// System information collector for enhanced error reporting

export interface SystemInfo {
  // Browser Information
  browser: {
    name: string;
    version: string;
    engine: string;
    userAgent: string;
    language: string;
    languages: string[];
    cookieEnabled: boolean;
    doNotTrack: string | null;
    hardwareConcurrency: number;
    maxTouchPoints: number;
    onLine: boolean;
    platform: string;
    vendor: string;
  };
  
  // Operating System
  os: {
    name: string;
    version: string;
    platform: string;
    architecture: string;
  };
  
  // Device Information
  device: {
    type: 'desktop' | 'mobile' | 'tablet';
    vendor: string;
    model: string;
    screen: {
      width: number;
      height: number;
      colorDepth: number;
      pixelDepth: number;
      pixelRatio: number;
      orientation: string;
    };
    viewport: {
      width: number;
      height: number;
    };
    memory?: {
      deviceMemory: number;
      hardwareConcurrency: number;
    };
  };
  
  // Network Information
  network: {
    connection: {
      effectiveType: string;
      downlink: number;
      rtt: number;
      saveData: boolean;
    };
    onLine: boolean;
  };
  
  // Performance Information
  performance: {
    memory?: {
      usedJSHeapSize: number;
      totalJSHeapSize: number;
      jsHeapSizeLimit: number;
    };
    timing: {
      navigationStart: number;
      loadEventEnd: number;
      domContentLoadedEventEnd: number;
      responseEnd: number;
      requestStart: number;
      responseStart: number;
    };
    navigation: {
      type: string;
      redirectCount: number;
    };
  };
  
  // Application Information
  app: {
    url: string;
    referrer: string;
    title: string;
    timestamp: string;
    sessionId: string;
    timezone: string;
    timezoneOffset: number;
  };
  
  // Feature Detection
  features: {
    localStorage: boolean;
    sessionStorage: boolean;
    indexedDB: boolean;
    serviceWorker: boolean;
    webGL: boolean;
    webGL2: boolean;
    webAudio: boolean;
    geolocation: boolean;
    notifications: boolean;
    pushManager: boolean;
    mediaDevices: boolean;
    getUserMedia: boolean;
    webRTC: boolean;
    webAssembly: boolean;
    sharedArrayBuffer: boolean;
    atob: boolean;
    btoa: boolean;
  };
  
  // Security Information
  security: {
    isSecureContext: boolean;
    referrerPolicy: string;
    permissions: {
      geolocation: string;
      notifications: string;
      camera: string;
      microphone: string;
    };
  };
}

// Browser detection with more detail
const getDetailedBrowserInfo = (): SystemInfo['browser'] => {
  const userAgent = navigator.userAgent;
  let browser = { name: 'Unknown', version: 'Unknown', engine: 'Unknown' };
  
  if (userAgent.includes('Chrome')) {
    browser = { 
      name: 'Chrome', 
      version: userAgent.match(/Chrome\/(\d+)/)?.[1] || 'Unknown', 
      engine: 'Blink' 
    };
  } else if (userAgent.includes('Firefox')) {
    browser = { 
      name: 'Firefox', 
      version: userAgent.match(/Firefox\/(\d+)/)?.[1] || 'Unknown', 
      engine: 'Gecko' 
    };
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = { 
      name: 'Safari', 
      version: userAgent.match(/Version\/(\d+)/)?.[1] || 'Unknown', 
      engine: 'WebKit' 
    };
  } else if (userAgent.includes('Edge')) {
    browser = { 
      name: 'Edge', 
      version: userAgent.match(/Edge\/(\d+)/)?.[1] || 'Unknown', 
      engine: 'EdgeHTML' 
    };
  } else if (userAgent.includes('MSIE') || userAgent.includes('Trident')) {
    browser = { 
      name: 'Internet Explorer', 
      version: userAgent.match(/(?:MSIE|rv:)\s*(\d+)/)?.[1] || 'Unknown', 
      engine: 'Trident' 
    };
  }
  
  return {
    ...browser,
    userAgent,
    language: navigator.language,
    languages: [...(navigator.languages || [])],
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    onLine: navigator.onLine,
    platform: navigator.platform,
    vendor: navigator.vendor
  };
};

// OS detection with architecture
const getDetailedOSInfo = (): SystemInfo['os'] => {
  const userAgent = navigator.userAgent;
  let os: Pick<SystemInfo['os'], 'name' | 'version' | 'platform'> = { name: 'Unknown', version: 'Unknown', platform: navigator.platform };
  
  if (userAgent.includes('Windows')) {
    os = { 
      name: 'Windows', 
      version: userAgent.match(/Windows NT (\d+\.\d+)/)?.[1] || 'Unknown', 
      platform: 'Win32' 
    };
  } else if (userAgent.includes('Mac')) {
    os = { 
      name: 'macOS', 
      version: userAgent.match(/Mac OS X (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown', 
      platform: 'MacIntel' 
    };
  } else if (userAgent.includes('Linux')) {
    os = { 
      name: 'Linux', 
      version: 'Unknown', 
      platform: 'Linux x86_64' 
    };
  } else if (userAgent.includes('Android')) {
    os = { 
      name: 'Android', 
      version: userAgent.match(/Android (\d+\.\d+)/)?.[1] || 'Unknown', 
      platform: 'Android' 
    };
  } else if (userAgent.includes('iOS')) {
    os = { 
      name: 'iOS', 
      version: userAgent.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown', 
      platform: 'iPhone' 
    };
  }
  
  return {
    ...os,
    architecture: navigator.platform.includes('64') ? '64-bit' : '32-bit'
  };
};

// Device detection with more details
const getDetailedDeviceInfo = (): SystemInfo['device'] => {
  const userAgent = navigator.userAgent;
  let device: SystemInfo['device'] = { 
    type: 'desktop', 
    vendor: 'Unknown', 
    model: 'Unknown',
    screen: {
      width: 0,
      height: 0,
      colorDepth: 0,
      pixelDepth: 0,
      pixelRatio: 1,
      orientation: 'unknown'
    },
    viewport: {
      width: 0,
      height: 0
    }
  };
  
  if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
    device.type = 'mobile';
    if (userAgent.includes('iPhone')) {
      device = { ...device, type: 'mobile', vendor: 'Apple', model: 'iPhone' };
    } else if (userAgent.includes('iPad')) {
      device = { ...device, type: 'tablet', vendor: 'Apple', model: 'iPad' };
    } else if (userAgent.includes('Android')) {
      device = { ...device, type: 'mobile', vendor: 'Android', model: 'Android Device' };
    }
  }
  
  const screenInfo = {
    width: window.screen.width,
    height: window.screen.height,
    colorDepth: window.screen.colorDepth,
    pixelDepth: window.screen.pixelDepth,
    pixelRatio: window.devicePixelRatio || 1,
    orientation: window.screen.orientation?.type || 'unknown'
  };
  
  const viewport = {
    width: window.innerWidth,
    height: window.innerHeight
  };
  
  const memory = (navigator as any).deviceMemory ? {
    deviceMemory: (navigator as any).deviceMemory,
    hardwareConcurrency: navigator.hardwareConcurrency || 0
  } : undefined;
  
  return {
    ...device,
    screen: screenInfo,
    viewport,
    memory
  };
};

// Network information
const getDetailedNetworkInfo = (): SystemInfo['network'] => {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  
  return {
    connection: {
      effectiveType: connection?.effectiveType || 'unknown',
      downlink: connection?.downlink || 0,
      rtt: connection?.rtt || 0,
      saveData: connection?.saveData || false
    },
    onLine: navigator.onLine
  };
};

// Performance information
const getDetailedPerformanceInfo = (): SystemInfo['performance'] => {
  const performance = window.performance;
  const memory = (performance as any).memory;
  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  
  return {
    memory: memory ? {
      usedJSHeapSize: memory.usedJSHeapSize,
      totalJSHeapSize: memory.totalJSHeapSize,
      jsHeapSizeLimit: memory.jsHeapSizeLimit
    } : undefined,
    timing: {
      navigationStart: performance.timing?.navigationStart || 0,
      loadEventEnd: performance.timing?.loadEventEnd || 0,
      domContentLoadedEventEnd: performance.timing?.domContentLoadedEventEnd || 0,
      responseEnd: performance.timing?.responseEnd || 0,
      requestStart: performance.timing?.requestStart || 0,
      responseStart: performance.timing?.responseStart || 0
    },
    navigation: {
      type: navigation?.type || 'unknown',
      redirectCount: navigation?.redirectCount || 0
    }
  };
};

// Feature detection
const getFeatureDetection = (): SystemInfo['features'] => {
  return {
    localStorage: !!window.localStorage,
    sessionStorage: !!window.sessionStorage,
    indexedDB: !!window.indexedDB,
    serviceWorker: 'serviceWorker' in navigator,
    webGL: !!window.WebGLRenderingContext,
    webGL2: !!window.WebGL2RenderingContext,
    webAudio: !!window.AudioContext,
    geolocation: 'geolocation' in navigator,
    notifications: 'Notification' in window,
    pushManager: 'PushManager' in window,
    mediaDevices: 'mediaDevices' in navigator,
    getUserMedia: 'getUserMedia' in navigator,
    webRTC: 'RTCPeerConnection' in window,
    webAssembly: 'WebAssembly' in window,
    sharedArrayBuffer: 'SharedArrayBuffer' in window,
    atob: 'atob' in window,
    btoa: 'btoa' in window
  };
};

// Security information
const getSecurityInfo = async (): Promise<SystemInfo['security']> => {
  const permissions = {
    geolocation: 'unknown',
    notifications: 'unknown',
    camera: 'unknown',
    microphone: 'unknown'
  };
  
  // Check permissions if available
  if ('permissions' in navigator) {
    try {
      const geolocationPermission = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
      permissions.geolocation = geolocationPermission.state;
    } catch (e) {
      // Permission API not supported
    }
    
    try {
      const notificationPermission = await navigator.permissions.query({ name: 'notifications' as PermissionName });
      permissions.notifications = notificationPermission.state;
    } catch (e) {
      // Permission API not supported
    }
  }
  
  return {
    isSecureContext: window.isSecureContext,
    referrerPolicy: (document as any).referrerPolicy || 'unknown',
    permissions
  };
};

// Generate session ID
const generateSessionId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Get session ID (create if doesn't exist)
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem('systemInfoSessionId');
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem('systemInfoSessionId', sessionId);
  }
  return sessionId;
};

// Main function to collect all system information
export const collectSystemInfo = async (): Promise<SystemInfo> => {
  const browser = getDetailedBrowserInfo();
  const os = getDetailedOSInfo();
  const device = getDetailedDeviceInfo();
  const network = getDetailedNetworkInfo();
  const performance = getDetailedPerformanceInfo();
  const features = getFeatureDetection();
  const security = await getSecurityInfo();
  const sessionId = getSessionId();
  
  return {
    browser,
    os,
    device,
    network,
    performance,
    features,
    security,
    app: {
      url: window.location.href,
      referrer: document.referrer,
      title: document.title,
      timestamp: new Date().toISOString(),
      sessionId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset()
    }
  };
};

export default collectSystemInfo; 